import Accordion from "@components/HourlyMain/Accordion";
import LayoutGrid from "@components/HomeMain/LayoutGrid";
import { homeStateSelector, setHomeState } from "@store/redux/modules/home";
import { useDispatch, useSelector } from "react-redux";
import { EarlyWarning } from "@components/HourlyMain/Default";
import { fetchDataMap } from "@/components/BasicLayout/BasicHeader/Navigation";
import { refreshUserLocation } from "@/components/BasicLayout/BasicHeader/LocationMask";
import { appThemeStyle } from "@/config";
import RainProb from "@assets/icons/Rain_Prob.svg?react";
import { isTestLink } from "@/utils";
import { Skeleton } from "antd-mobile";
import ErrorPage from "@/components/ErrorPage";
import { getWeekName } from "@/components/HomeMain/RecentWeather";
import useDayjs from "@/hooks/useDayjs";
import useDateFormat from "@/hooks/useDateFormat";
import lodash from "lodash";
import { useTranslation } from "react-i18next";
import { useCallbackState } from "@/utils/hook";
const { dayjs, userTimeZone } = useDayjs();
interface AccordionItem {
  EpochDateTime: number;
  DateShort: string;
  isDayTime: number;
  Day: {
    WeatherIconImg: string;
    WeatherText: string;
    LongPhrase?: string;
    PrecipitationProbability?: number;
  };
  Night: {
    WeatherIconImg: string;
    WeatherText: string;
    LongPhrase?: string;
    PrecipitationProbability?: number;
  };
  Temperature: {
    Maximum: { Value: number; Unit: string };
    Minimum: { Value: number; Unit: string };
  };
  warnings?: Array<{
    Description: { Localized: string };
    detail: { start: string; end: string };
    Color: { Name: string };
  }>;
  Items?: any[];
  isExpand: boolean;
}

const DailyDefault: React.FC = () => {
  const navigate = useNavigate();
  const homeState = useSelector(homeStateSelector);
  const weatherStatus = homeState?.info?.weatherStatus || LocalStorage.getItem("info")?.weatherStatus;
  const tipsStyle = appThemeStyle?.[weatherStatus]?.tipsStyle ?? {};
  const [dayList, setDayList] = useCallbackState([]);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const localSelectCity = LocalStorage.getItem("select_city");
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [keyId, setKeyId] = useState(0);
  const fetchData = async (keyId: any = undefined) => {
    try {
      const resData = await dailyApi.query({
        test: isDevEnv ? 1 : undefined,
        keyId: keyId || localSelectCity?.Key || homeState?.info?.key || LocalStorage.getItem("info")?.key,
      });
      if (!resData?.data?.dayList) return;
      const formattedDayList: AccordionItem[] =
        resData?.data?.dayList?.map((item: any) => ({
          ...item,
          isExpand: false,
        })) || [];
      LocalStorage.setItem("info", resData.data.baseInfo);
      dispatch(setHomeState({ info: resData.data.baseInfo }));
      setDayList(
        () => formattedDayList,
        () => {
          setKeyId(() => ++keyId);
        }
      );
    } catch (error: any) {
      setIsShowErrorPage(() => true);
      setErrorMessage(() => error.message);
    }
  };
  fetchDataMap[pathname] = fetchData;
  const keyDependency = homeState?.info?.key || LocalStorage.getItem("info")?.key;

  useEffect(() => {
    if (!(keyDependency || localSelectCity?.Key)) {
      refreshUserLocation(dispatch);
    } else {
      fetchData();
    }
  }, [keyDependency]);

  const handleSearchDetail = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`detail/${index}`, {
      replace: false,
      state: {
        index,
        dayList,
      },
    });
  };
  const { shortDate } = useDateFormat();
  const { t: $t } = useTranslation();

  const getDayOrNight = (item: any, isDayTime: number) => {
    return item[isDayTime === 1 ? "Day" : "Night"];
  };

  const renderTitle = (item: any, index: number, handleClick: (item: any, index: number) => void) => {
    const weekDayName = getWeekName(item.EpochDateTime);
    const dayOrNight = getDayOrNight(item, item.isDayTime);

    return (
      <div className="flex justify-between items-center text-12 gap-x-10" onClick={() => handleClick(item, index)}>
        <div className="text-10 text-center flex-[1]">
          <p>{weekDayName ?? item.Week}</p>
          <p>
            {item.EpochDateTime &&
              (userTimeZone ? dayjs.unix(item.EpochDateTime).tz(userTimeZone).format(shortDate) : item.DateShort)}
          </p>
        </div>

        <div className="w-20">
          <WeatherImage className="w-full" src={dayOrNight.WeatherIconImg} />
        </div>
        <div className="flex-[4] w-120">{dayOrNight.WeatherText}</div>
        <div className="text-16 flex-[2]">
          {item?.Temperature?.Maximum?.Value}
          {item?.Temperature?.Maximum?.Unit}/{item?.Temperature?.Minimum?.Value}
          {item?.Temperature?.Minimum?.Unit}
        </div>
        <div className="flex items-center gap-x-5 flex-[1.5]">
          <RainProb />
          <div>{dayOrNight.PrecipitationProbability}%</div>
        </div>
        <div className="center flex-[1]">
          <SvgIcon name={item?.isExpand ? "arrow_up" : "arrow_down"} />
        </div>
      </div>
    );
  };

  const renderContent = (item: any, index: number) => {
    const dayOrNight = getDayOrNight(item, item.isDayTime);
    return (
      <>
        <div className="flex flex-col gap-y-8">
          {dayOrNight?.LongPhrase && (
            <div
              className="bg-[rgba(13,3,66,0.63)] rounded-8 font-weight-300 text-10 color-[rgba(255,255,255,0.7)] lh-13 p-8 box-border"
              style={tipsStyle}
            >
              {dayOrNight?.LongPhrase}
            </div>
          )}
          {!!item?.warnings?.length &&
            item.warnings.map((item: any, index: number) => {
              const warning = appThemeStyle?.[weatherStatus]?.warning?.[item?.Color?.Name?.toLocaleLowerCase()] ?? {};
              return (
                <EarlyWarning
                  key={index}
                  title={item.Description?.Localized}
                  content={item?.detail?.start + "-" + item?.detail?.end}
                  onClick={() => {}}
                  color={warning?.fontColor}
                  bgColor={tipsStyle?.background}
                  fillColor={warning?.fillColor}
                />
              );
            })}
          {!!item?.Items?.length && (
            <LayoutGrid
              list={item.Items}
              style={{ background: "transparent", backdropFilter: "none", boxShadow: "none" }}
            />
          )}
          <div className="center" onClick={event => handleSearchDetail(index, event)}>
            <div>{$t("title05")}</div>
            <SvgIcon name="arrow_right" />
          </div>
        </div>
      </>
    );
  };

  const skeletonLargeStyle = {
    "--width": "100%",
    "--height": "363px",
    "--border-radius": "10px",
  };

  const skeletonSmallStyle = {
    "--width": "100%",
    "--height": "55px",
    "--border-radius": "10px",
  };

  const skeletonItems = Array.from({ length: 9 }, (_, index) => index);

  function renderSkeletons() {
    return (
      <>
        <Skeleton animated style={skeletonLargeStyle} />
        {skeletonItems.map(index => (
          <Skeleton key={`skeleton-${index}`} animated style={skeletonSmallStyle} />
        ))}
      </>
    );
  }

  function renderAccordion(dayList: any[], keyId: any) {
    return (
      <Accordion
        key={keyId}
        list={dayList}
        setLocalList={setDayList}
        renderTitle={renderTitle}
        renderContent={renderContent}
      />
    );
  }

  const hostname = window.location.hostname;
  function shouldRenderAd(domains: string[], isTestLink: (domains: string[]) => boolean, hostname: string): boolean {
    if (!Array.isArray(domains) || domains.length === 0 || typeof hostname !== "string") {
      return false;
    }
    return isTestLink(domains) || domains.includes(hostname);
  }

  const wiDomains = domainMap["wi"] || [];
  const wgDomains = domainMap["wg"] || [];
  const otherDomains = lodash.omit(domainMap, ["wi"]);
  const flattenedOtherDomains = lodash.flatten(Object.values(otherDomains));

  const ad1Domains = [...wiDomains, ...wgDomains];
  const ad2Domains = flattenedOtherDomains;

  const shouldRenderAd1 = shouldRenderAd(ad1Domains, isTestLink, hostname);
  const shouldRenderAd2 = shouldRenderAd(ad2Domains, isTestLink, hostname);

  const handleRefreshPage = () => {
    setIsShowErrorPage(() => false);
    fetchData();
  };

  return isShowErrorPage ? (
    <ErrorPage errorMessage={errorMessage} refreshPage={handleRefreshPage} />
  ) : (
    <div className="flex flex-col justify-start gap-y-10 p-[0_16px] box-border">
      {shouldRenderAd1 && <GoogleAd {...useGoogleAd("320x100", 2)} />}
      {shouldRenderAd2 && <GoogleAd {...useGoogleAd("300x250", 0)} />}
      {isEmptyType(dayList) ? renderSkeletons() : renderAccordion(dayList, keyId)}
    </div>
  );
};
export default DailyDefault;
